import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FeatureFlags } from '~constants/featureFlags';
import useRutterItemsQuery from '~queries/useRutterItemsQuery';
import useRutterLineItemMatchesQuery, {
  rutterLineItemMatchesQueryKey,
} from '~queries/useRutterLineItemMatchesQuery';
import { Nullable } from '~types/Nullable';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';

import { QuickBooksItemLine, RutterLineItemMatch } from './models';
import useRutter from './useRutter';

const MATCH_POLL_INTERVAL_MS = 3000;
// Matching is an LLM call per settlement; stop waiting well past its normal runtime.
const MATCH_POLL_TIMEOUT_MS = 30000;

export interface QuickBooksItemOption {
  id: string;
  name: string;
}

export default function useSettlementQuickBooksItems(
  settlementId: string,
  isReadOnly: boolean,
) {
  const queryClient = useQueryClient();
  const isItemMatchingEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.qbItemMatchLlm,
  });
  const { createLineItemMatch, enqueueLineItemMatching, updateLineItemMatch } =
    useRutter();
  const itemsQuery = useRutterItemsQuery({ enabled: isItemMatchingEnabled });
  const connectionId = itemsQuery.data?.connectionId ?? null;
  const items = itemsQuery.data?.items;
  const isColumnVisible =
    isItemMatchingEnabled && Boolean(connectionId) && (items?.length ?? 0) > 0;

  // `pendingMatchIds` are the lines the run rewrites; it is done once each has a newer
  // result. `hadNoMatchRecords` covers a settlement the matcher has never seen, whose
  // records only appear once it ran. A run the backend started for new add-ons is done
  // once each of `awaitedAddOnChargeIds` has a record. Without `canFinishFromData` only
  // the timeout ends the run, since no record it can see will change.
  const [matchingRun, setMatchingRun] = useState<{
    awaitedAddOnChargeIds: string[];
    baselineUpdatedAtByMatchId: Map<string, number>;
    canFinishFromData: boolean;
    hadNoMatchRecords: boolean;
    pendingMatchIds: string[];
    settlementId: string;
    startedAt: number;
  } | null>(null);
  const isMatching = matchingRun !== null;
  const hasAutoRunRef = useRef(false);
  const matchesQuery = useRutterLineItemMatchesQuery({
    connectionId: isColumnVisible ? connectionId : null,
    refetchIntervalMs: isMatching ? MATCH_POLL_INTERVAL_MS : false,
    settlementId,
  });
  const matches = matchesQuery.data;
  const matchesRef = useRef(matches);
  matchesRef.current = matches;

  useEffect(() => {
    hasAutoRunRef.current = false;
    setMatchingRun(null);
  }, [settlementId]);

  const runMatching = useCallback(async () => {
    if (!connectionId || isReadOnly) {
      return;
    }

    const currentMatches = matchesRef.current ?? [];
    // the matcher rewrites every line but a manual pick, so a manual line never gets newer
    const pendingMatchIds = currentMatches
      .filter((match) => !match.isManual)
      .map((match) => match.id);
    // when every record is a manual pick, a line the matcher adds a record for is the only
    // change, and nothing here knows which lines still lack one
    const isEveryMatchManual = currentMatches.length > 0 && pendingMatchIds.length === 0;

    setMatchingRun({
      awaitedAddOnChargeIds: [],
      baselineUpdatedAtByMatchId: new Map(
        currentMatches.map((match) => [match.id, match.updatedAt.valueOf()]),
      ),
      canFinishFromData: !isEveryMatchManual,
      hadNoMatchRecords: currentMatches.length === 0,
      pendingMatchIds,
      settlementId,
      startedAt: Date.now(),
    });
    const isEnqueued = await enqueueLineItemMatching(connectionId, settlementId);

    if (!isEnqueued) {
      setMatchingRun((run) => (run?.settlementId === settlementId ? null : run));
    }
  }, [connectionId, enqueueLineItemMatching, isReadOnly, settlementId]);

  // Adding an add-on to the settlement queues its matching on the backend, so this only
  // waits for the new add-on's record to show up.
  const awaitAddOnMatching = useCallback(
    (addOnChargeIds: string[]) => {
      if (!isColumnVisible || isReadOnly || addOnChargeIds.length === 0) {
        return;
      }

      const currentMatches = matchesRef.current ?? [];

      // a run already matching other lines keeps waiting on them as well
      setMatchingRun((run) => ({
        awaitedAddOnChargeIds: [
          ...(run?.settlementId === settlementId ? run.awaitedAddOnChargeIds : []),
          ...addOnChargeIds,
        ],
        baselineUpdatedAtByMatchId:
          run?.settlementId === settlementId ? run.baselineUpdatedAtByMatchId : new Map(),
        canFinishFromData:
          run?.settlementId === settlementId ? run.canFinishFromData : true,
        hadNoMatchRecords:
          run?.settlementId === settlementId
            ? run.hadNoMatchRecords
            : currentMatches.length === 0,
        pendingMatchIds: run?.settlementId === settlementId ? run.pendingMatchIds : [],
        settlementId,
        startedAt: Date.now(),
      }));
    },
    [isColumnVisible, isReadOnly, settlementId],
  );

  // Opening the line items matches every line once, but only when something still needs an
  // item, so a settlement that is already matched is not re-rolled. Later item-less
  // matches (a cleared pick, a new add-on) never trigger a full run.
  useEffect(() => {
    if (hasAutoRunRef.current || !matches || isReadOnly) {
      return;
    }

    hasAutoRunRef.current = true;
    const someLineNeedsItem =
      matches.length === 0 || matches.some((match) => !match.rutterItemId);

    if (someLineNeedsItem) {
      void runMatching();
    }
  }, [isReadOnly, matches, runMatching]);

  // A timer, not a data check, so a failing endpoint cannot keep the polling alive.
  useEffect(() => {
    if (!matchingRun) {
      return;
    }

    const remainingMs = Math.max(
      0,
      matchingRun.startedAt + MATCH_POLL_TIMEOUT_MS - Date.now(),
    );
    const timeoutId = setTimeout(() => {
      setMatchingRun(null);
      // a job that finished just after the deadline is still shown
      void queryClient.invalidateQueries({
        queryKey: rutterLineItemMatchesQueryKey(connectionId, matchingRun.settlementId),
      });
    }, remainingMs);

    return () => clearTimeout(timeoutId);
  }, [connectionId, matchingRun, queryClient]);

  useEffect(() => {
    if (!matchingRun?.canFinishFromData || !matches) {
      return;
    }

    const updatedAtByMatchId = new Map(
      matches.map((match) => [match.id, match.updatedAt.valueOf()]),
    );
    const hasResultForEveryPendingLine = matchingRun.pendingMatchIds.every(
      (matchId) =>
        (updatedAtByMatchId.get(matchId) ?? 0) >
        (matchingRun.baselineUpdatedAtByMatchId.get(matchId) ?? 0),
    );
    const hasRecordsNowThatThereAreSome =
      !matchingRun.hadNoMatchRecords || matches.length > 0;
    const matchedAddOnChargeIds = new Set(matches.map((match) => match.addOnChargeId));
    const hasRecordForEveryAwaitedAddOn = matchingRun.awaitedAddOnChargeIds.every(
      (addOnChargeId) => matchedAddOnChargeIds.has(addOnChargeId),
    );
    const isRunDone =
      hasResultForEveryPendingLine &&
      hasRecordsNowThatThereAreSome &&
      hasRecordForEveryAwaitedAddOn;

    if (isRunDone) {
      setMatchingRun(null);
    }
  }, [matches, matchingRun]);

  const itemOptions: QuickBooksItemOption[] = useMemo(() => {
    return (items ?? [])
      .filter((item) => item.isSelectable)
      .map((item) => ({ id: item.id, name: item.rutterApiItem?.name ?? '' }))
      .sort((optionA, optionB) => optionA.name.localeCompare(optionB.name));
  }, [items]);

  const matchesByAddOnChargeId = useMemo(() => {
    const byAddOnChargeId = new Map<string, RutterLineItemMatch[]>();

    (matches ?? []).forEach((match) => {
      if (match.addOnChargeId) {
        byAddOnChargeId.set(match.addOnChargeId, [match]);
      }
    });

    return byAddOnChargeId;
  }, [matches]);

  const hasAiSuggestedMatch = (matches ?? []).some((match) => match.isAiSuggested);

  const matchesBySettlementLineItemId = useMemo(() => {
    const bySettlementLineItemId = new Map<string, RutterLineItemMatch>();

    (matches ?? []).forEach((match) => {
      if (match.settlementLineItemId) {
        bySettlementLineItemId.set(match.settlementLineItemId, match);
      }
    });

    return bySettlementLineItemId;
  }, [matches]);

  const saveItem = useCallback(
    async (
      rowMatches: RutterLineItemMatch[],
      rowLines: QuickBooksItemLine[],
      option: Nullable<QuickBooksItemOption>,
    ) => {
      const rutterItemId = option?.id ?? null;
      const unmatchedLines = rowLines.filter(
        (line) =>
          !rowMatches.some(
            (match) =>
              (line.settlementLineItemId &&
                match.settlementLineItemId === line.settlementLineItemId) ||
              (line.addOnChargeId && match.addOnChargeId === line.addOnChargeId),
          ),
      );
      const [updatedMatches, createdMatches] = await Promise.all([
        Promise.all(
          rowMatches.map((match) => updateLineItemMatch(match.id, rutterItemId)),
        ),
        Promise.all(
          unmatchedLines.map((line) =>
            connectionId ? createLineItemMatch(connectionId, line, rutterItemId) : null,
          ),
        ),
      ]);

      // the backend broadcasts a settlement update after each save, which refreshes
      // the needs-item count through the settlements realtime subscription
      const savedMatchesById = new Map(
        updatedMatches.flatMap((savedMatch) =>
          savedMatch ? [[savedMatch.id, savedMatch]] : [],
        ),
      );
      const newMatches = createdMatches.flatMap((createdMatch) =>
        createdMatch ? [createdMatch] : [],
      );
      queryClient.setQueryData<RutterLineItemMatch[]>(
        rutterLineItemMatchesQueryKey(connectionId, settlementId),
        (cachedMatches) => {
          // an evicted cache stays empty so the refetch supplies the full list
          if (!cachedMatches) {
            return undefined;
          }

          // a matcher run may have created the same record while the pick was saving
          const createdMatchIds = new Set(newMatches.map((match) => match.id));
          const keptMatches = cachedMatches
            .filter((match) => !createdMatchIds.has(match.id))
            .map((match) => savedMatchesById.get(match.id) ?? match);

          return [...keptMatches, ...newMatches];
        },
      );

      const didEverySaveSucceed =
        updatedMatches.every(Boolean) && createdMatches.every(Boolean);

      if (!didEverySaveSucceed) {
        // some lines may have saved before another failed; show the real per-line state
        await queryClient.invalidateQueries({
          queryKey: rutterLineItemMatchesQueryKey(connectionId, settlementId),
        });
      }

      return didEverySaveSucceed;
    },
    [connectionId, createLineItemMatch, queryClient, settlementId, updateLineItemMatch],
  );

  return {
    areMatchesLoaded: matchesQuery.isSuccess,
    awaitAddOnMatching,
    hasAiSuggestedMatch,
    isColumnVisible,
    isMatching,
    itemOptions,
    matchesByAddOnChargeId,
    matchesBySettlementLineItemId,
    runMatching,
    saveItem,
  };
}
